import React, { useEffect, useMemo, useState } from "react";
import { Alert, Autocomplete, Box, Button, Card, Stack, TextField } from "@mui/material";
import { DataGrid, GridToolbarContainer, GridToolbarExport } from "@mui/x-data-grid";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import { useSnackbar } from "notistack";
import RevenueMonthFilter from "../../campaigns/common/RevenueMonthFilter";
import CampaignForm from "../common/Form/CampaignForm";
import SummaryTile from "./SummaryTile";
import { getRevenueBudgetReportColumns } from "../../../utils/revenueBudgetReportColumns";
import paymentportalservice from "../../../services/paymentportalservice";
import { useDebounce } from "../../../hooks/useDebounce";
import helpers from "../../../hooks/HelperFunctions";

function CustomToolbar() {
    return (
        <GridToolbarContainer sx={{ justifyContent: "space-between", px: 1.5, py: 1 }}>
            <GridToolbarExport />
        </GridToolbarContainer>
    );
}

const CARD_SX = {
    p: { xs: 1.75, sm: 2.5 },
    borderRadius: 3,
    border: "1px solid rgba(0,0,0,0.06)",
    bgcolor: "#fff",
    boxShadow: 0,
};

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = String(new Date().getMonth() + 1).padStart(2, "0");

export default function RevenueBudgetReportTab() {
    const { enqueueSnackbar } = useSnackbar();
    const [monthYear, setMonthYear] = useState({ month: CURRENT_MONTH, year: CURRENT_YEAR });
    const [poc, setPoc] = useState(null);
    const [pocOptions, setPocOptions] = useState([]);
    const [search, setSearch] = useState("");
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Reuses the same "Add Campaign" drawer as components/payments/Campaign.jsx's widget —
    // CampaignForm owns its own super-admin-only revenue/salesperson fields, and this whole
    // page is already super-admin-gated (see PaymentsReports.jsx), so no extra check needed
    // here beyond just rendering it.
    const [openCampaignForm, setOpenCampaignForm] = useState(false);
    const handleOpenCampaignForm = () => setOpenCampaignForm(true);
    const handleCloseCampaignForm = () => setOpenCampaignForm(false);
    // CampaignForm's own {severity, message} callback shape, adapted onto the snackbar
    // already mounted at the app root (see App.js's SnackbarProvider) instead of adding a
    // second, page-local snackbar just for this.
    const handleSnackBarOpen = (severity, message) => enqueueSnackbar(message, { variant: severity });
    // Bumped after a successful create so the report re-fetches and shows the new campaign
    // immediately, without needing its own separate refetch function.
    const [refreshKey, setRefreshKey] = useState(0);

    const debouncedSearch = useDebounce(search, 400);
    const columns = useMemo(() => getRevenueBudgetReportColumns(), []);

    // POC options don't depend on the selected month/year — fetched once, same
    // as PaymentStatus.jsx's own POC dropdown.
    useEffect(() => {
        paymentportalservice
            .FetchRevenueBudgetReportFilters()
            .then((data) => setPocOptions(data?.pocs || []))
            .catch((error) => console.error("Error fetching Revenue & Budget report POC filters:", error));
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        paymentportalservice
            .FetchRevenueBudgetReport({
                month: monthYear.month,
                year: monthYear.year,
                poc: poc?.id,
                search: debouncedSearch,
            })
            .then((data) => {
                if (!cancelled) setReport(data);
            })
            .catch((err) => {
                console.error("Error fetching Revenue & Budget report:", err);
                if (!cancelled) setError(helpers.describeApiError(err));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [monthYear, poc, debouncedSearch, refreshKey]);

    return (
        <Stack rowGap={2.5}>
            {error && <Alert severity="error">{error}</Alert>}

            <Stack direction="row" flexWrap="wrap" columnGap={2} rowGap={2}>
                <SummaryTile label="Total Revenue" value={report?.totalRevenue} icon={<TrendingUpRoundedIcon />} />
                <SummaryTile
                    label="Total Net Revenue"
                    value={report?.totalNetRevenue}
                    icon={<SavingsRoundedIcon />}
                />
                <SummaryTile
                    label="Total Budget"
                    value={report?.totalBudget}
                    icon={<AccountBalanceWalletRoundedIcon />}
                />
                <SummaryTile label="Total Limit" value={report?.totalLimit} icon={<SpeedRoundedIcon />} />
            </Stack>

            <Card sx={CARD_SX}>
                <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={2} rowGap={1.5} sx={{ mb: 2 }}>
                    <RevenueMonthFilter
                        value={monthYear}
                        onApply={(month, year) => setMonthYear({ month, year })}
                        onClear={() => setMonthYear({ month: CURRENT_MONTH, year: CURRENT_YEAR })}
                    />

                    <Autocomplete
                        size="small"
                        sx={{ minWidth: 220 }}
                        options={pocOptions}
                        getOptionLabel={(option) => option.name || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={poc}
                        onChange={(e, value) => setPoc(value)}
                        renderInput={(params) => <TextField {...params} label="POC" placeholder="Any" />}
                    />

                    <TextField
                        size="small"
                        sx={{ minWidth: 240 }}
                        label="Search campaign / brand"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />

                    <Box sx={{ flexGrow: 1 }} />

                    <Button
                        variant="contained"
                        startIcon={<AddCircleOutlineRoundedIcon />}
                        onClick={handleOpenCampaignForm}
                        sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, boxShadow: "none" }}
                    >
                        Add Campaign
                    </Button>
                </Stack>

                <Box sx={{ width: "100%" }}>
                    <DataGrid
                        autoHeight
                        loading={loading}
                        columns={columns}
                        rows={report?.campaigns || []}
                        getRowId={(row) => row._id}
                        density="compact"
                        showCellVerticalBorder
                        showColumnVerticalBorder
                        slots={{ toolbar: CustomToolbar }}
                        initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
                        pageSizeOptions={[25, 50, 100]}
                        sx={{ border: "none" }}
                    />
                </Box>
            </Card>

            <CampaignForm
                openCampaignForm={openCampaignForm}
                handleOpenCampaignForm={handleOpenCampaignForm}
                handleCloseCampaignForm={handleCloseCampaignForm}
                handleSnackBarOpen={handleSnackBarOpen}
                updateLatestCampaign={() => setRefreshKey((key) => key + 1)}
            />
        </Stack>
    );
}
